import {
	UploadInfo,
	UploadRequestParams,
	UploadSpecification,
	ZoneService,
} from '@citrite/sf-api';
import {
	type ActionSet,
	type ProjectSpec,
	type ProjectSpecDataProviderProps,
	PermissionPolicy,
} from '@sharefiledev/projects-pilet-sdk';
import {
	BulkAddProjectUserPayload,
	ClientDelegationPayload,
	GetProjectsResult,
	ProjectDto,
	ProjectLifeCycleStage,
	ProjectStatus,
	QueryProjectsByTagsPayload,
	ShareFileUserDto,
	SortDirection,
	SortField,
} from '../types';
import { ProjectDataProvider } from './ProjectDataProvider';

export const defaultActionSet: ActionSet = {
	[PermissionPolicy.CanAddProjectUsers]: { enabled: false },
	[PermissionPolicy.CanDeleteProject]: { enabled: false },
	[PermissionPolicy.CanDeleteProjectFile]: { enabled: false },
	[PermissionPolicy.CanRemoveProjectUsers]: { enabled: false },
	[PermissionPolicy.CanRestoreProject]: { enabled: false },
	[PermissionPolicy.CanRestoreProjectFile]: { enabled: false },
	[PermissionPolicy.CanUpdateProject]: { enabled: true },
	[PermissionPolicy.CanViewDeletedFilesInProject]: { enabled: false },
	[PermissionPolicy.CanDownloadProjectFile]: { enabled: false },
	[PermissionPolicy.CanDownloadProjectFileRAMOriginal]: { enabled: false },
	[PermissionPolicy.CanShareFileRAM]: { enabled: false },
	[PermissionPolicy.CanFreezeProject]: { enabled: false },
	[PermissionPolicy.CanUnfreezeProject]: { enabled: false },
};

export function useProjectSpecDataProvider({
	resourceSpec: projectSpec,
	onUpdate: updateCallback,
}: ProjectSpecDataProviderProps): ProjectDataProvider {
	const projectSpecToDto = (spec: ProjectSpec): ProjectDto => {
		return {
			accountId: 'sharefile:account-1',
			id: '1',
			name: spec.name,
			description: spec.description,
			projectUsers: [],
			createdOnUtc: new Date(),
			updatedOnUtc: new Date(),
			resourceId: 'sharefile:account-1/project-1',
			status: ProjectStatus.Open,
			enableFileBrowser: spec.enableFileBrowser,
			version: 1,
			lifeCycleStage: ProjectLifeCycleStage.Active,
			actions: defaultActionSet,
		};
	};

	const projectToSpec = ({
		name,
		description,
		projectUsers,
		enableFileBrowser,
	}: ProjectDto): ProjectSpec => ({
		name,
		description,
		users: projectUsers?.map(u => ({ id: u.user.id, role: u.role })),
		solutionId: projectSpec.solutionId,
		enableFileBrowser,
		sendNotification: projectSpec.sendNotification,
	});

	return {
		async createProject(
			_project: Pick<ProjectDto, 'name' | 'description' | 'enableFileBrowser'>
		): Promise<ProjectDto> {
			throw new Error('Method not implemented');
		},

		async getPagedProjects(_request) {
			throw new Error('Method not implemented');
		},

		async getEntitlements() {
			throw new Error('Method not implemented');
		},

		async getSortedProjectsWithEntitlements(
			_containerRID: string,
			_sortField: SortField,
			_sortDirection: SortDirection,
			_status?: ProjectStatus,
			_lifeCycleStages?: ProjectLifeCycleStage[]
		) {
			throw new Error('Method not implemented');
		},

		async getSortedProjectsWithEntitlementsAndTags(
			_containerRID: string,
			_sortField: SortField,
			_sortDirection: SortDirection,
			_status?: ProjectStatus,
			_lifeCycleStages?: ProjectLifeCycleStage[],
			_userRIDs?: string[]
		) {
			throw new Error('Method not implemented');
		},

		async getSortedProjectsByContainerRID(
			_containerRID: string,
			_sortField: SortField,
			_sortDirection: SortDirection,
			_status?: ProjectStatus,
			_lifeCycleStages?: ProjectLifeCycleStage[]
		) {
			throw new Error('Method not implemented');
		},

		async getDeletedProjects(_containerRID: string) {
			throw new Error('Method not implemented');
		},

		async getProjectById(_projectId: string) {
			return projectSpecToDto(projectSpec);
		},

		async putProject(project: ProjectDto) {
			const updatedProject = {
				...projectToSpec(project),
			};
			updateCallback(updatedProject);
			return project;
		},

		async deleteProject(_projectId: string) {
			throw new Error('Method not implemented');
		},

		async getProjectUsers(_projectId: string) {
			throw new Error('Method not implemented');
		},

		async addUsersToProject(_projectId: string, _payload: BulkAddProjectUserPayload) {
			throw new Error('Method not implemented');
		},

		async addDelegatingClient(_projectId: string, _payload: ClientDelegationPayload) {
			throw new Error('Method not implemented');
		},

		async restoreProjectById(_projectId: string) {
			throw new Error('Method not implemented');
		},

		async getProjectFiles(_projectId: string) {
			throw new Error('Method not implemented');
		},

		async getDeletedProjectFiles(_projectId: string) {
			throw new Error('Method not implemented');
		},

		async deleteProjectFiles(_projectId: string, _fileIds: string[]) {
			throw new Error('Method not implemented');
		},

		async restoreProjectFiles(_projectId: string, _fileIds: string[]) {
			throw new Error('Method not implemented');
		},

		async getProjectFilesBulkDownloadUrl(_projectId: string, _fileIds: string[]) {
			throw new Error('Method not implemented');
		},

		async getRoleByUser(_userId: string) {
			throw new Error('Method not implemented');
		},

		async uploadFile(
			_projectId: string,
			_requestingResourceId: string,
			_uploadRequestParams: UploadRequestParams
		): Promise<UploadSpecification> {
			throw new Error('Method not implemented');
		},

		async uploadFileComplete(
			_projectId: string,
			_requestingResourceId: string,
			_uploadInfo: UploadInfo
		) {
			throw new Error('Method not implemented');
		},

		async createClient(_client: ShareFileUserDto) {
			throw new Error('Method not implemented');
		},

		async getProjectZoneServices(_projectId: string): Promise<ZoneService> {
			throw new Error('Method not implemented');
		},

		async getProjectContacts(_rid: string): Promise<ShareFileUserDto[]> {
			throw new Error('Method not implemented');
		},

		async freezeProject(_projectId: string) {
			throw new Error('Method not implemented');
		},

		async unfreezeProject(_projectId: string) {
			throw new Error('Method not implemented');
		},

		async queryProjectsByTags(
			_payload: QueryProjectsByTagsPayload,
			_containerRIDPrefix: string,
			_sortField?: SortField,
			_sortDirection?: SortDirection,
			_status?: ProjectStatus,
			_lifeCycleStages?: ProjectLifeCycleStage[]
		): Promise<GetProjectsResult> {
			throw new Error('Method not implemented');
		},
	};
}
